import { createCipheriv, createDecipheriv, randomBytes } from 'node:crypto';
import {
  base64UrlDecode,
  base64UrlEncode,
  E2EE_NONCE_BYTES,
  isE2eeEnvelope,
  type E2eeEnvelope,
  type E2eeHandshake,
} from '@buildautomaton/e2ee';
import type { CliE2eeKey } from './cli-e2ee-key.js';

export type CliE2eeRuntime = {
  keyId: string;
  handshake: E2eeHandshake;
  encryptFields: (message: Record<string, unknown>, fields: readonly string[]) => Record<string, unknown>;
  decryptMessage: (message: Record<string, unknown>) => Record<string, unknown>;
  /** Decrypt a standalone {@link E2eeEnvelope} (e.g. prompt image ciphertext). */
  decryptEnvelopeToBuffer: (envelope: E2eeEnvelope) => Buffer;
};

function nonceFromCounter(prefix: Buffer, counter: bigint): Buffer {
  const nonce = Buffer.alloc(E2EE_NONCE_BYTES);
  prefix.copy(nonce, 0);
  nonce.writeBigUInt64BE(counter, 4);
  return nonce;
}

export function createCliE2eeRuntime(key: CliE2eeKey): CliE2eeRuntime {
  const rawKey = Buffer.from(base64UrlDecode(key.k));
  const prefix = randomBytes(4);
  let counter = 0n;

  function nextNonce(): Buffer {
    counter += 1n;
    return nonceFromCounter(prefix, counter);
  }

  function encryptObject(messageWithoutSensitiveFields: Record<string, unknown>, plaintext: Record<string, unknown>): E2eeEnvelope {
    const nonce = nextNonce();
    const cipher = createCipheriv('aes-256-gcm', rawKey, nonce);
    const ciphertext = Buffer.concat([cipher.update(JSON.stringify(plaintext), 'utf8'), cipher.final()]);
    const tag = cipher.getAuthTag();
    return {
      k: key.id,
      n: base64UrlEncode(nonce),
      c: base64UrlEncode(Buffer.concat([ciphertext, tag])),
    };
  }

  return {
    keyId: key.id,
    handshake: { k: key.id, a: key.a },
    encryptFields(message, fields) {
      const plaintext: Record<string, unknown> = {};
      const stripped = { ...message };
      let hasPlaintext = false;
      for (const field of fields) {
        if (Object.prototype.hasOwnProperty.call(stripped, field) && stripped[field] !== undefined) {
          plaintext[field] = stripped[field];
          delete stripped[field];
          hasPlaintext = true;
        }
      }
      if (!hasPlaintext) return message;
      stripped.ee = encryptObject(stripped, plaintext);
      return stripped;
    },
    decryptMessage(message) {
      const envelope = message.ee;
      if (!isE2eeEnvelope(envelope)) return message;
      if (envelope.k !== key.id) throw new Error(`E2EE key mismatch: ${envelope.k}`);
      const sealed = Buffer.from(base64UrlDecode(envelope.c));
      if (sealed.length < 16) throw new Error('Invalid E2EE payload.');
      const ciphertext = sealed.subarray(0, sealed.length - 16);
      const tag = sealed.subarray(sealed.length - 16);
      const nonce = Buffer.from(base64UrlDecode(envelope.n));
      const decipher = createDecipheriv('aes-256-gcm', rawKey, nonce);
      decipher.setAuthTag(tag);
      const decrypted = Buffer.concat([decipher.update(ciphertext), decipher.final()]).toString('utf8');
      const parsed = JSON.parse(decrypted) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('E2EE payload did not decode to an object.');
      }
      const merged = { ...message, ...(parsed as Record<string, unknown>) };
      delete merged.ee;
      return merged;
    },
    decryptEnvelopeToBuffer(envelope: E2eeEnvelope) {
      if (envelope.k !== key.id) throw new Error(`E2EE key mismatch: ${envelope.k}`);
      const sealed = Buffer.from(base64UrlDecode(envelope.c));
      if (sealed.length < 16) throw new Error('Invalid E2EE payload.');
      const ciphertext = sealed.subarray(0, sealed.length - 16);
      const tag = sealed.subarray(sealed.length - 16);
      const nonce = Buffer.from(base64UrlDecode(envelope.n));
      const decipher = createDecipheriv('aes-256-gcm', rawKey, nonce);
      decipher.setAuthTag(tag);
      return Buffer.concat([decipher.update(ciphertext), decipher.final()]);
    },
  };
}
